import { h } from 'preact'
import { useState, useEffect, useCallback, useRef } from 'preact/hooks'
import PropTypes from 'prop-types'

import { useLiveKit } from 'lib/videoCallHooks'
import { usePortal } from 'lib/portal'
import classNames from 'lib/classNames'
import Icon from 'components/Icon'

import ParticipantGrid from 'components/VideoCall/ParticipantGrid'
import ParticipantListPanel from 'components/VideoCall/ParticipantListPanel'
import InMeetingChat from 'components/VideoCall/InMeetingChat'
import CallControls from 'components/VideoCall/CallControls'
import JoinPage from 'components/VideoCall/JoinPage'

import './index.sass'

export default function ActiveCallOverlay({ callData, onLeave, myDisplayName, isAdmin: propIsAdmin }) {
  const { roomName, callType, callerName, chatName } = callData

  const [isChatOpen, setIsChatOpen] = useState(false)
  const [isParticipantPanelOpen, setIsParticipantPanelOpen] = useState(false)
  const [unreadMessages, setUnreadMessages] = useState(0)
  const [showLobby, setShowLobby] = useState(!!callData.showLobby)
  const [lobbyError, setLobbyError] = useState(null)
  const [callEnded, setCallEnded] = useState(false)
  const lastMessageCountRef = useRef(0)
  const connectCalledRef = useRef(false)
  const renderPortal = usePortal()

  const isAdmin = propIsAdmin !== undefined ? propIsAdmin : !!callData.isInitiator

  const livekit = useLiveKit({
    onCallEnded() {
      onLeave()
    },
    onError(msg) {
      console.error('Call error:', msg)
      setLobbyError(msg)
    },
    onKickedOut() {
      onLeave()
    },
  })

  const connectRef = useRef(livekit.connect)
  connectRef.current = livekit.connect

  const endCallRef = useRef(livekit.endCall)
  endCallRef.current = livekit.endCall

  const previousPathRef = useRef(window.location.pathname)
  useEffect(() => {
    if (!roomName) return
    const callPath = `/video-call/${roomName}`
    if (!window.location.pathname.startsWith('/video-call/')) {
      previousPathRef.current = window.location.pathname
      window.history.pushState(null, '', callPath)
    }
  }, [roomName])

  useEffect(() => {
    if (roomName && !connectCalledRef.current && !showLobby) {
      connectCalledRef.current = true
      connectRef.current(roomName, callType, myDisplayName)
    }
  }, [roomName, showLobby])

  useEffect(() => {
    return () => {
      endCallRef.current()
    }
  }, [])

  const handleLobbyJoin = useCallback(({ displayName, cameraOn, micOn }) => {
    setShowLobby(false)
    setLobbyError(null)
    connectCalledRef.current = true
    connectRef.current(roomName, callType, displayName || myDisplayName, { cameraOn, micOn })
  }, [roomName, callType, myDisplayName])

  useEffect(() => {
    const count = livekit.chatMessages.length
    if (count > lastMessageCountRef.current && !isChatOpen) {
      setUnreadMessages(prev => prev + (count - lastMessageCountRef.current))
    }
    lastMessageCountRef.current = count
  }, [livekit.chatMessages.length, isChatOpen])

  useEffect(() => {
    if (isChatOpen) setUnreadMessages(0)
  }, [isChatOpen])

  const handleEndCall = useCallback(async () => {
    await livekit.endCall()
    setCallEnded(true)
    setTimeout(() => {
      onLeave()
    }, 2500)
  }, [livekit.endCall, onLeave])

  const handleToggleChat = useCallback(() => {
    setIsChatOpen(open => !open)
  }, [])

  const handleToggleParticipantPanel = useCallback(() => {
    setIsParticipantPanelOpen(open => !open)
  }, [])

  const handleKickParticipant = useCallback((identity) => {
    if (livekit.kickParticipant) livekit.kickParticipant(identity)
  }, [livekit.kickParticipant])

  const handleMuteParticipant = useCallback((identity) => {
    if (livekit.muteParticipant) livekit.muteParticipant(identity)
  }, [livekit.muteParticipant])

  const handleAddVirtualParticipant = useCallback(() => {
    if (livekit.addVirtualParticipant) livekit.addVirtualParticipant()
  }, [livekit.addVirtualParticipant])

  const overlayClass = classNames('ActiveCallOverlay', {
    chatOpen: isChatOpen,
  })

  const connected = livekit.isConnected

  if (callEnded) {
    const endedContent = <div className={overlayClass}>
      <div className="ActiveCallOverlay-ended">
        <div className="ActiveCallOverlay-endedIcon">
          <Icon type={callType === 'video' ? 'video' : 'audio'} size="hg" />
        </div>
        <h2 className="ActiveCallOverlay-endedTitle">Call Ended</h2>
        <p className="ActiveCallOverlay-endedSubtitle">You have left the call</p>
        <p className="ActiveCallOverlay-endedHint">Returning you back…</p>
      </div>
    </div>
    return renderPortal(endedContent)
  }

  if (showLobby) {
    const isRealHost = callerName && callerName !== myDisplayName
    const content = <div className={overlayClass}>
      <JoinPage
        meetingName={chatName || roomName}
        hostName={isRealHost ? callerName : null}
        callType={callType}
        participantCount={0}
        displayName={myDisplayName}
        onJoin={handleLobbyJoin}
        isJoining={livekit.isConnecting}
        error={lobbyError}
      />
    </div>
    return renderPortal(content)
  }

  const content = <div className={overlayClass}>
    <div className="ActiveCallOverlay-main">
      <div className="ActiveCallOverlay-header">
        <span className="ActiveCallOverlay-roomName">
          <Icon type={callType === 'video' ? 'video' : 'audio'} size="sm" />
          {' '}{chatName || (callType === 'video' ? 'Video Call' : 'Audio Call')}
        </span>
        <span
          className="ActiveCallOverlay-participantCount"
          onClick={handleToggleParticipantPanel}
          role="button"
          tabIndex={0}
        >
          <Icon type="user-list" size="sm" />
          {' '}{connected ? (1 + livekit.participants.length) : '...'}
        </span>

        {/* Virtual participant button (testing) */}
        {isAdmin && connected && (
          <button
            className="ActiveCallOverlay-virtualBtn"
            onClick={handleAddVirtualParticipant}
            title="Add virtual participant (testing)"
          >
            <Icon type="user-list" size="sm" />
            <span>+ Virtual</span>
          </button>
        )}
      </div>

      <div className="ActiveCallOverlay-grid">
        {!connected
          ? <div className="ActiveCallOverlay-connecting">
            <div className="ActiveCallOverlay-connectingIcon">
              <Icon type={callType === 'video' ? 'video' : 'audio'} size="hg" />
            </div>
            <h2 className="ActiveCallOverlay-connectingTitle">
              {livekit.error ? 'Connection Error' : 'Joining Call…'}
            </h2>
            <p className="ActiveCallOverlay-connectingSubtitle">
              {livekit.error || 'Setting up your audio and video'}
            </p>
          </div>
          : <ParticipantGrid
            localVideoTrack={livekit.localVideoTrack}
            localAudioTrack={livekit.localAudioTrack}
            localScreenTrack={livekit.localScreenTrack}
            localIdentity={livekit.localIdentity}
            isMicMuted={livekit.isMicMuted}
            isVideoEnabled={livekit.isVideoEnabled}
            isScreenSharing={livekit.isScreenSharing}
            participants={livekit.participants}
            isAdmin={isAdmin}
            onKickParticipant={handleKickParticipant}
            onMuteParticipant={handleMuteParticipant}
          />
        }
      </div>

      <CallControls
        isMicMuted={livekit.isMicMuted}
        isVideoEnabled={livekit.isVideoEnabled}
        isScreenSharing={livekit.isScreenSharing}
        isChatOpen={isChatOpen}
        callQuality={livekit.callQuality}
        onToggleMute={livekit.toggleMute}
        onToggleVideo={livekit.toggleVideo}
        onToggleScreenShare={livekit.toggleScreenShare}
        onToggleChat={handleToggleChat}
        onEndCall={handleEndCall}
        chatMessageCount={unreadMessages}
      />
    </div>

    {isChatOpen && <InMeetingChat
      chatMessages={livekit.chatMessages}
      sendChatMessage={livekit.sendChatMessage}
      isOpen={isChatOpen}
      onClose={handleToggleChat}
    />}

    {isParticipantPanelOpen && connected && <ParticipantListPanel
      participants={livekit.participants}
      localIdentity={livekit.localIdentity}
      localDisplayName={myDisplayName}
      isMicMuted={livekit.isMicMuted}
      isVideoEnabled={livekit.isVideoEnabled}
      isScreenSharing={livekit.isScreenSharing}
      onClose={handleToggleParticipantPanel}
      isAdmin={isAdmin}
      onKickParticipant={handleKickParticipant}
      onMuteParticipant={handleMuteParticipant}
    />}
  </div>

  return renderPortal(content)
}

ActiveCallOverlay.propTypes = {
  callData: PropTypes.shape({
    roomName: PropTypes.string.isRequired,
    token: PropTypes.string,
    wsUrl: PropTypes.string,
    identity: PropTypes.string,
    callType: PropTypes.oneOf(['video', 'audio']).isRequired,
    callerName: PropTypes.string,
    chatName: PropTypes.string,
    showLobby: PropTypes.bool,
    isInitiator: PropTypes.bool,
  }).isRequired,
  onLeave: PropTypes.func.isRequired,
  myDisplayName: PropTypes.string,
  isAdmin: PropTypes.bool,
}
